import { Components, Theme } from '@mui/material'

declare module '@mui/material/IconButton' {
  interface IconButtonOwnProps {
    // IconButton doesn't have variants in MUI, so adding them is different
    variant?: 'contained' | 'ghost'
  }
}
const loadingIndicatorSelector = '& .MuiIconButton-loadingIndicator'

export const MuiIconButton: Components<Omit<Theme, 'components'>>['MuiIconButton'] = {
  defaultProps: {
    disableRipple: true,
    variant: 'ghost',
    color: 'secondary',
  },
  styleOverrides: {
    root: {
      '&.Mui-disabled': {
        opacity: 0.4,
      },
      // Hide whatever the button renders while loading, not just SvgIcons
      '&.MuiIconButton-loading > *:not(.MuiIconButton-loadingWrapper)': {
        opacity: 0,
      },
      // Icon always the same size
      '& .MuiSvgIcon-root': {
        fontSize: '1.25rem',
      },
    },
  },
  variants: [
    {
      props: { variant: 'contained' },
      style: {},
    },
    {
      props: { variant: 'ghost' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.invisible,
      }),
    },
    // Colour
    {
      props: { color: 'primary', variant: 'contained' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.brand.default,
        color: theme.palette.text.brand.onBrand,
        '&:hover': {
          backgroundColor: theme.palette.bg.brand.hover,
        },
        [`&.Mui-disabled, ${loadingIndicatorSelector}`]: {
          backgroundColor: theme.palette.bg.brand.default,
          color: theme.palette.text.brand.onBrand,
        },
      }),
    },
    {
      props: { color: 'primary', variant: 'ghost' },
      style: ({ theme }) => ({
        color: theme.palette.text.brand.default,
        '&:hover': {
          backgroundColor: theme.palette.bg.brand.muted,
          color: theme.palette.text.brand.onBrandMuted,
        },
        [`&.Mui-disabled, ${loadingIndicatorSelector}`]: {
          color: theme.palette.text.brand.default,
        },
      }),
    },
    {
      props: { color: 'secondary', variant: 'contained' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.surface.elevated,
        borderStyle: 'solid',
        borderWidth: theme.strokeWidth.default,
        borderColor: theme.palette.border.subtle,
        color: theme.palette.text.muted,
        boxShadow: theme.customShadows.buttonDrop,
        '&:hover': {
          borderColor: theme.palette.border.strong,
          boxShadow: [theme.customShadows.buttonInner, theme.customShadows.buttonDrop].join(', '),
        },
        [`&.Mui-disabled, ${loadingIndicatorSelector}`]: {
          backgroundColor: theme.palette.bg.surface.elevated,
          color: theme.palette.text.default,
          boxShadow: [theme.customShadows.buttonInner, theme.customShadows.buttonDrop].join(', '),
        },
      }),
    },
    {
      props: { color: 'secondary', variant: 'ghost' },
      style: ({ theme }) => ({
        color: theme.palette.text.muted,
        '&:hover': {
          color: theme.palette.text.default,
          backgroundColor: theme.palette.bg.interactive.hover,
        },
        [`&.Mui-disabled, ${loadingIndicatorSelector}`]: {
          color: theme.palette.text.default,
        },
      }),
    },
    {
      props: { color: 'error', variant: 'contained' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.danger.default,
        color: theme.palette.text.danger.onDanger,
        '&:hover': {
          backgroundColor: theme.palette.bg.danger.hover,
        },
        [`&.Mui-disabled, ${loadingIndicatorSelector}`]: {
          backgroundColor: theme.palette.bg.danger.default,
          color: theme.palette.text.danger.onDanger,
        },
      }),
    },
    {
      props: { color: 'error', variant: 'ghost' },
      style: ({ theme }) => ({
        color: theme.palette.text.danger.default,
        '&:hover': {
          color: theme.palette.text.danger.onDangerMuted,
          backgroundColor: theme.palette.bg.danger.muted,
        },
        [`&.Mui-disabled, ${loadingIndicatorSelector}`]: {
          color: theme.palette.text.danger.onDangerMuted,
        },
      }),
    },
    // Size
    {
      props: { size: 'large' },
      style: ({ theme }) => ({
        borderRadius: theme.shape['radius-lg'],
        padding: theme.spacing(2.5),
      }),
    },
    {
      props: { size: 'medium' },
      style: ({ theme }) => ({
        borderRadius: theme.shape['radius-lg'],
        padding: theme.spacing(2),
      }),
    },
    {
      props: { size: 'small' },
      style: ({ theme }) => ({
        borderRadius: theme.shape['radius-md'],
        padding: theme.spacing(1),
      }),
    },
  ],
}
